import { Add, DescriptionOutlined } from '@mui/icons-material'
import { useTheme } from '@mui/material'
import { Context, memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { permissionValue } from 'src/enums/permissionValue'
import { ILocality } from 'src/generated-types'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { CreateDocumentDialog, ICreateDocumentsParams } from '../../documents/components/CreateDocumentDialog'
import DocumentsInEquipment from './DocumentsInEquipment'
import EquipmentSectionLayout from './EquipmentSectionLayout'

interface Props {
  context: Context<any> | null | undefined
  customerId: EntityId
  localityId: EntityId | undefined
  mainComponentId: any
  mainComponent: IMainComponent | undefined
  locality: ILocality | undefined
  isReadOnly?: boolean
  reportCount: number
  onDocumentCountChange: (length: number) => void
  onContentHeightChange?: (height: number) => void
}

const DocumentSection = memo((props: Props) => {
  const {
    context,
    mainComponent,
    mainComponentId,
    customerId,
    localityId,
    isReadOnly,
    reportCount,
    onDocumentCountChange,
    onContentHeightChange,
  } = props
  const { showDialog } = useDialogCtx()
  const { t } = useTranslation(['main-component'])
  const theme = useTheme()
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!contentNode || !onContentHeightChange) return

    let frame: number
    const observer = new ResizeObserver(([entry]) => {
      const contentHeight = entry.contentRect.height
      frame = requestAnimationFrame(() => onContentHeightChange(contentHeight))
    })
    observer.observe(contentNode)

    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [contentNode, onContentHeightChange])

  const onAddDocumentForComponent = async () => {
    const documentParams = {
      mainComponentId: mainComponentId as EntityId,
      mooringId: mainComponent?.position?.mooringId as EntityId,
      positionId: mainComponent?.positionId,
      mooringName: mainComponent?.position?.mooringName as string,
    } as ICreateDocumentsParams
    const added = await showDialog(
      CreateDocumentDialog,
      {
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          createDocumentsParams: documentParams,
        },
        pop: () => {
          invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId as EntityId, { mainComponentId })
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }

  return (
    <EquipmentSectionLayout
      title={t('documents', { ns: 'document' })}
      icon={<DescriptionOutlined sx={{ fontSize: '18px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
      buttonText={t('documents', { ns: 'document' })}
      buttonIcon={<Add />}
      onButtonClick={onAddDocumentForComponent}
      context={context}
      permissions={[
        permissionValue.Locality.MainComponent.Document.Write,
        permissionValue.ClientAdminPermission,
        permissionValue.ServiceStation.Write,
        permissionValue.ClientAdminPortal.Equipment_Portal,
      ]}
      content={
        <DocumentsInEquipment
          {...props}
          isReadOnly={isReadOnly}
          autoheight={true}
          maximumContentHeight={window.innerHeight - 300}
          minimalEmptyStatus={reportCount < 4}
          setDataLength={onDocumentCountChange}
          contentRef={setContentNode}
        />
      }
      isReadOnly={isReadOnly}
    />
  )
})

export default DocumentSection
